"use client"

import * as React from "react"
import { Link } from "react-router"

import { GettingStartedSetupFlow } from "@/components/onboarding"
import { EvaluationDashboardOnboardingSheets } from "@/components/evaluation-dashboard/evaluation-dashboard-onboarding-sheets"
import { StatusBadge } from "@/components/ui/status-badge"
import { Button } from "@/components/ui/button"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import type { EvaluationTerm } from "@/lib/mock/evaluation-dashboard"
import { useProduct } from "@/contexts/product-context"
import { useSurveysFirstTermSetup } from "@/hooks/use-surveys-first-term-setup"
import {
  isSurveysOnboardingPath,
  saveSurveysOnboardingCompleted,
  surveysOnboardingNextTasks,
  surveysOnboardingPathLabel,
  type SurveysOnboardingNextTask,
  type SurveysOnboardingPath,
  type SurveysOnboardingSheetId,
} from "@/lib/evaluation-dashboard-onboarding"
import type { Product } from "@/stores/app-store"
import { cn } from "@/lib/utils"

export type SurveysFirstTermSetupBag = ReturnType<typeof useSurveysFirstTermSetup>

interface EvaluationDashboardOnboardingSectionProps {
  product?: Product
  setup?: SurveysFirstTermSetupBag
  currentTerm?: EvaluationTerm
}

function HideGuideButton({
  label,
  onClick,
}: {
  label: string
  onClick: () => void
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8 shrink-0"
          aria-label={label}
          onClick={onClick}
        >
          <i className="fa-light fa-xmark" aria-hidden="true" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top">{label}</TooltipContent>
    </Tooltip>
  )
}

/** Calendar + check + sparkles. Status-badge success tokens, same language as product-home covers. */
function FirstTermCompleteArt() {
  return (
    <svg
      viewBox="0 0 168 108"
      className="h-[6.75rem] w-[10.5rem] text-status-badge-success-fg"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="138" cy="22" r="32" className="fill-current opacity-20" />
      <circle cx="22" cy="86" r="20" className="fill-current opacity-15" />
      <path
        d="M146 14l2.2 5.4 5.4 2.2-5.4 2.2-2.2 5.4-2.2-5.4-5.4-2.2 5.4-2.2z"
        className="fill-current"
      />
      <path
        d="M18 24l1.6 4 4 1.6-4 1.6-1.6 4-1.6-4-4-1.6 4-1.6z"
        className="fill-current opacity-80"
      />
      <rect
        x="40"
        y="24"
        width="78"
        height="68"
        rx="12"
        className="fill-card stroke-current"
        strokeWidth="1.6"
      />
      <clipPath id="first-term-complete-cal">
        <rect x="40" y="24" width="78" height="68" rx="12" />
      </clipPath>
      <rect
        x="40"
        y="24"
        width="78"
        height="18"
        className="fill-current"
        clipPath="url(#first-term-complete-cal)"
      />
      <rect x="56" y="16" width="7" height="14" rx="3" className="fill-current" />
      <rect x="95" y="16" width="7" height="14" rx="3" className="fill-current" />
      <circle cx="58" cy="58" r="4" className="fill-current" />
      <circle cx="79" cy="58" r="4" className="fill-current" />
      <circle cx="100" cy="58" r="4" className="fill-current" />
      <circle cx="58" cy="76" r="4" className="fill-current" />
      <circle cx="79" cy="76" r="4" className="fill-current" />
      <circle cx="128" cy="76" r="20" className="fill-current" />
      <path
        d="M118 76l7 7 14-16"
        className="stroke-status-badge-success-fill"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function FirstTermCompleteCard({
  pathLabel,
  stepCount,
  onHide,
}: {
  pathLabel?: string
  stepCount: number
  onHide: () => void
}) {
  const meta = pathLabel
    ? `${pathLabel} · ${stepCount} of ${stepCount} complete`
    : `${stepCount} of ${stepCount} complete`

  return (
    <div className="relative overflow-hidden rounded-xl border border-status-badge-success-fg/25 bg-status-badge-success-fill animate-in fade-in-0 duration-300">
      <div className="flex items-start gap-3 p-4 sm:gap-4 sm:p-5">
        <span
          className="flex size-11 shrink-0 items-center justify-center rounded-full bg-status-badge-success-fg text-status-badge-success-fill sm:hidden"
          aria-hidden="true"
        >
          <i className="fa-solid fa-calendar-check" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-heading text-lg font-semibold tracking-tight text-foreground">
              First term is set up
            </h2>
            <StatusBadge label="Complete" tone="success" size="md" icon="fa-circle-check" />
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{meta}</p>
          <ul className="mt-3 flex flex-wrap gap-1.5" aria-hidden="true">
            {Array.from({ length: stepCount }, (_, index) => (
              <li
                key={index}
                className="flex size-6 items-center justify-center rounded-full bg-status-badge-success-fg text-status-badge-success-fill"
              >
                <i className="fa-solid fa-check text-xs" />
              </li>
            ))}
          </ul>
        </div>
        <div className="hidden shrink-0 self-center sm:block" aria-hidden="true">
          <FirstTermCompleteArt />
        </div>
        <HideGuideButton label="Hide first term setup" onClick={onHide} />
      </div>
    </div>
  )
}

function NextTasksList({
  tasks,
  onDismiss,
}: {
  tasks: SurveysOnboardingNextTask[]
  onDismiss: () => void
}) {
  return (
    <section
      aria-labelledby="surveys-onboarding-next-tasks-heading"
      className="flex flex-col gap-3"
    >
      <div className="flex items-center justify-between gap-3">
        <h3
          id="surveys-onboarding-next-tasks-heading"
          className="text-sm font-medium text-foreground"
        >
          Next up
        </h3>
        <HideGuideButton label="Hide next tasks" onClick={onDismiss} />
      </div>
      <ul className="flex flex-col gap-2">
        {tasks.map(task => (
          <li key={task.title}>
            <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 gap-3">
                <span
                  className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground"
                  aria-hidden="true"
                >
                  <i className={cn("fa-light text-sm", task.iconClass)} />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">{task.title}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground sm:text-sm">
                    {task.description}
                  </p>
                </div>
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="shrink-0 gap-2"
                asChild
              >
                <Link to={task.href}>
                  <i className={cn("fa-light text-xs", task.iconClass)} aria-hidden="true" />
                  {task.actionLabel}
                </Link>
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}

/** Checklist and next tasks while first term setup runs. */
export function EvaluationDashboardOnboardingSection({
  product: productProp,
  setup: setupProp,
}: EvaluationDashboardOnboardingSectionProps) {
  const { product: contextProduct } = useProduct()
  const product = productProp ?? contextProduct
  const internalSetup = useSurveysFirstTermSetup(product)
  const setup = setupProp ?? internalSetup
  const {
    path,
    setPath,
    steps,
    completed,
    setCompleted,
    isChecklistComplete,
    showSetupGuide,
    showNextTasks,
    dismissSetupGuide,
    dismissNextTasks,
  } = setup

  const [activeSheet, setActiveSheet] = React.useState<SurveysOnboardingSheetId | null>(null)
  const [activeStepIndex, setActiveStepIndex] = React.useState<number | null>(null)
  const pathRef = React.useRef(path)
  pathRef.current = path

  const nextTasks = React.useMemo(() => surveysOnboardingNextTasks(product), [product])

  const handleStepAction = React.useCallback(
    (stepIndex: number, step: { sheetId?: string }) => {
      if (!step.sheetId) return
      setActiveStepIndex(stepIndex)
      setActiveSheet(step.sheetId as SurveysOnboardingSheetId)
    },
    [],
  )

  const handleSheetComplete = React.useCallback((completedPath?: SurveysOnboardingPath) => {
    if (activeStepIndex == null) return
    const activePath = isSurveysOnboardingPath(completedPath) ? completedPath : pathRef.current
    if (!activePath) return
    const next =
      activeStepIndex === 0 ? new Set<number>([0]) : new Set(completed).add(activeStepIndex)
    saveSurveysOnboardingCompleted(activePath, next)
    setCompleted(next)
    setActiveSheet(null)
    setActiveStepIndex(null)
  }, [activeStepIndex, completed, setCompleted])

  const showChecklist = showSetupGuide && !isChecklistComplete
  const showDoneCard = showSetupGuide && isChecklistComplete

  return (
    <>
      <div className="flex min-w-0 flex-col gap-6">
        {showDoneCard ? (
          <FirstTermCompleteCard
            pathLabel={path ? surveysOnboardingPathLabel(path) : undefined}
            stepCount={steps.length}
            onHide={dismissSetupGuide}
          />
        ) : null}

        {showChecklist ? (
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-1">
                <h2 className="font-heading text-xl font-semibold tracking-tight text-foreground">
                  Set up your first term
                </h2>
                <p className="text-sm text-muted-foreground">
                  {path
                    ? `${surveysOnboardingPathLabel(path)} · ${completed.size} of ${steps.length} completed`
                    : `${completed.size} of ${steps.length} completed`}
                </p>
              </div>
              <div
                className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
                aria-hidden="true"
              >
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${steps.length > 0 ? (completed.size / steps.length) * 100 : 0}%`,
                    background: "var(--brand-color)",
                  }}
                />
              </div>
            </div>

            <GettingStartedSetupFlow
              steps={steps}
              title="Your steps"
              showTitle={false}
              showProgressMeta={false}
              completed={completed}
              onCompletedChange={setCompleted}
              onStepAction={handleStepAction}
            />
          </div>
        ) : null}

        {showNextTasks ? (
          <NextTasksList tasks={nextTasks} onDismiss={dismissNextTasks} />
        ) : null}
      </div>

      {showChecklist ? (
        <EvaluationDashboardOnboardingSheets
          activeSheet={activeSheet}
          path={path}
          onPathChange={setPath}
          onOpenChange={open => {
            if (!open) {
              setActiveSheet(null)
              setActiveStepIndex(null)
            }
          }}
          onComplete={handleSheetComplete}
        />
      ) : null}
    </>
  )
}
